import { useTranslation } from "react-i18next";
import { Image, TouchableHighlight, View, ViewStyle } from "react-native";
import { AccessibilityType } from "../../../../../central-atoms/accessibility/enums";
import { ChargePointStatus } from "../../../../../central-atoms/enums/ocpp/v1.6/generic";
import { ChargePoint } from "../../../../../central-atoms/types/charging/charge-points";
import { MergedConnector } from "../../../../../central-atoms/types/charging/connectors";
import { COLORS } from "../../../../../config/colors";
import { DEFAULT_PLUG_IMG } from "../../../../../config/setting";
import { horizontalSize } from "../../../../../functions/responsive";
import { CheckIcon, LockIcon } from "../../../../../icons";
import Pill from "../../../../../miscellaneous/components/Pill";
import { StyleHelpers } from "../../../../../miscellaneous/constants/style-helpers";
import { THEME_COLORS } from "../../../../../theme/constants/themeColors";
import { THEME_STYLES } from "../../../../../theme/constants/themeStyles";
import CText from "../../../../components/CText";
import CPConnectorStatusChip from "../ConnectorStatusChip";
import styles from "./styles";

type Props = {
  unifiedCPConnector: MergedConnector;
  chargePoint: ChargePoint;
  name?: string;
  selected?: boolean;
  showStatus?: boolean;
  isSelectable: boolean;
  onPress?: () => void;
  inuse?: string;
  style?: ViewStyle;
};

export default function CPConnectorCard(props: Props) {
  const { t } = useTranslation();

  const {
    unifiedCPConnector: { cp_connector, cs_connector },
    chargePoint,
    name = "",
    selected,
    showStatus = true,
    isSelectable,
    onPress = () => { },
    style = {},
  } = props;

  const canSelect =
    cp_connector?.status === ChargePointStatus.Available ||
    cp_connector?.status === ChargePointStatus.Preparing;

  const showPowerRating = !!cp_connector.power_rating && cp_connector.power_rating !== "0";

  return (
    <TouchableHighlight
      activeOpacity={1}
      underlayColor={
        isSelectable && canSelect ? THEME_COLORS.cardSelectedBgUnderlayColor : THEME_COLORS.cardUnderlayBgColor
      }
      style={{
        ...StyleHelpers.themeBorder,
        backgroundColor: selected ? THEME_COLORS.cardSelectedBgColor : THEME_COLORS.cardUnselectedBgColor,
        borderColor: selected ? THEME_COLORS.cardSelectedBorderColor : THEME_COLORS.cardUnselectedBorderColor,
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 12,
        alignItems: "center",
        justifyContent: "space-between",
        ...style,
      }}
      onPress={() => {
        if (isSelectable && canSelect) {
          onPress();
        }
      }}
    >
      <>
        <View style={{ width: "100%", justifyContent: "center" }}>
          {/* qr code id */}
          {!!chargePoint?.qr_code_id && (
            <CText
              value={chargePoint?.qr_code_id}
              size={10}
              semiBold
              style={{
                alignSelf: "center",
                color: THEME_COLORS.textColor,
                textAlign: "center",
                maxWidth: "60%",
                fontWeight: "600",
              }}
            />
          )}

          <View
            style={{
              position: 'absolute',
              top: -5,
              left: -4,
              minWidth: 20,
              minHeight: 20,
              paddingHorizontal: 4,
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1,
              ...THEME_STYLES.connectorId,
              ...StyleHelpers.themeBorder,
              borderColor: selected
                ? COLORS.whiteColor
                : COLORS.grey700,
            }}>
            <CText
              semiBold
              size={10}
              style={{
                // color: COLORS.whiteColor,
                // marginTop: isIOS() ? 2 : 3,
                // marginBottom: isIOS() ? 2 : 0,
                // lineHeight: 22,
              }}>
              {cp_connector.connector_id}
            </CText>
          </View>

          {/* name */}
          {!!name && (
            <CText
              value={name}
              size={9}
              semiBold
              style={{
                alignSelf: "center",
                marginTop: 5,
                color: THEME_COLORS.textColor,
                textAlign: "center",
                maxWidth: "60%",
                fontWeight: "600",
              }}
            />
          )}
          <View style={{ alignItems: "center" }}>
            <View
              style={{
                padding: 16,
                borderWidth: 1,
                borderColor: selected ? THEME_COLORS.primaryColor : COLORS.lightGrey,
                borderRadius: 100,
                marginTop: 12,
                backgroundColor: COLORS.whiteColor,
              }}
            >
              <Image
                source={{
                  uri: cs_connector?.connector?.image_url || DEFAULT_PLUG_IMG,
                }}
                style={{
                  height: 38,
                  width: 38,
                  borderRadius: 6,
                }}
                resizeMode="contain"
              />
            </View>
            {showStatus && (
              <CPConnectorStatusChip
                status={cp_connector.status}
                style={{
                  marginTop: -12,
                }}
              />
            )}
          </View>
        </View>

        <View style={{
          width: '100%',
        }}>
          {/* connector type */}
          <CText
            size={15}
            semiBold
            style={{
              marginTop: 24,
              color: THEME_COLORS.textColor,
              textAlign: "center",
              fontWeight: "600",
            }}
          >
            {cp_connector.type}
          </CText>
          {/* price */}
          <View style={{ flexDirection: "row", ...StyleHelpers.totalCenter }}>
            <CText
              size={13}
              style={{
                color: selected ? THEME_COLORS.textColor : THEME_COLORS.subTextColor,
                textAlign: "center",
                fontWeight: '600',
                marginTop: 4,
              }}
            >
              {showPowerRating && `${cp_connector.power_rating} kW`}
              {showPowerRating && !!cp_connector.price && ` · `}
              {!!cp_connector.price && cp_connector.price}
            </CText>
          </View>

          {isSelectable && canSelect && (
            <View
              style={{ 
                marginTop: 16, marginBottom: 4, alignItems: "center", 
              }}
            >
              {selected ? (
                <View
                  style={[
                    styles.btnCont,
                    styles.rowStyle,
                    {
                      paddingVertical: 8,
                      alignItems: "center",
                      backgroundColor: selected ? COLORS.dividerGrey : THEME_COLORS.buttonBgColor,
                      // borderRadius: 0,
                      // width: horizontalSize(35),
                      width: '100%',
                      justifyContent: "center",
                    },
                  ]}
                >
                  <CheckIcon
                    fill={THEME_COLORS.cardSelectedTextColor}
                    style={{
                      height: 12,
                      width: 12,
                    }}
                  />

                  <CText
                    value={t("selected")}
                    size={14}
                    // semiBold
                    style={{
                      fontWeight: '600',
                      color: THEME_COLORS.cardSelectedTextColor,
                      // marginTop: 2,
                      textAlign: "center",
                      marginLeft: 8,
                    }}
                  />
                </View>
              ) : (
                <View style={[styles.btnCont, { paddingVertical: 8 }]}>
                  <CText
                    value={t("select")}
                    size={14}
                    // semiBold
                    style={{
                      fontWeight: '600',
                      color: COLORS.whiteColor,
                      textAlign: "center",
                      // marginTop: 2,
                    }}
                  />
                </View>
              )}
            </View>
          )}
        </View>

        {chargePoint?.accessibility === AccessibilityType.Private && (
          <Pill
            flag={t("Private")}
            style={{
              backgroundColor: COLORS.cyan400,
              top: -14,
            }}
            textStyle={{
              color: COLORS.blackColor,
            }}
            icon={
              <LockIcon
                fill={COLORS.blackColor}
                height={12}
                width={12}
                style={{
                  marginRight: 6,
                }}
              />
            }
          />
        )}
      </>
    </TouchableHighlight>
  );
}
